import { selectedAccountSelector, selectedAccStateSelector } from '@core/store/library-stores';
import { useAppDispatch, useAppSelector } from '@core/store/store';
import { Typography } from '@material-ui/core';
import { isEmpty } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { PegasusFlowTable } from './PegasusFlowTable';
import { formatLatestDateReceived } from './PegasusTables.utils';
import { PegasusTimeTable } from './PegasusTimeTable';
import { LoadingData, useFormatDate } from '@hwm/ui-lib';
import { Trans } from '@lingui/macro';
import { PegasusDataModel } from '@shared/models';
import { preferencesSelector } from '@core/store/entity/preferences/PreferencesSelectors';
import { accountsThunks } from '@core/store/library-stores/accounts.thunks';

type BreakpointsType = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface PegasusTablesProps {
  displayAsColBreakpoint?: BreakpointsType;
  customPegasusData?: PegasusDataModel;
  isForPmzDetails?: boolean;
  /**
   * `dataTestId` - name of a pegasus tables (preferably `<name>-pegasus`)
   * @example
   * ```tsx
   * <PegasusTables {...props} dataTestId="test-pegasus" />
   */
  dataTestId: string;
}

export const PegasusTables: React.FC<PegasusTablesProps> = ({
  displayAsColBreakpoint = 'md',
  customPegasusData,
  isForPmzDetails = false,
  dataTestId,
}) => {
  const dispatch = useAppDispatch();
  const { formatDate } = useFormatDate();

  const { preferences: units } = useAppSelector(preferencesSelector);
  const { id, isPmZone } = useAppSelector(selectedAccountSelector);
  const { pmZoneData } = useAppSelector(selectedAccStateSelector);

  useEffect(() => {
    if (!id || !isPmZone || !units || customPegasusData) return;

    dispatch(accountsThunks.getPmZoneData({ accountId: id, units }));
  }, [dispatch, id, units, isPmZone, customPegasusData]);

  const pegasusData = customPegasusData ?? pmZoneData;
  const { pegasusStatus, pegasusFlowStatuses, pegasusTimeStatuses } = pegasusData ?? {};
  const { isFlowType, isTimeType, isTimeInterpolateType } = pegasusStatus ?? {};

  const showTimePegasusTable = useMemo(
    () => (isTimeType || isTimeInterpolateType) && pegasusTimeStatuses && !isEmpty(pegasusTimeStatuses),
    [isTimeType, isTimeInterpolateType, pegasusTimeStatuses]
  );

  const showFlowPegasusTable = useMemo(
    () => isFlowType && pegasusFlowStatuses && !isEmpty(pegasusFlowStatuses),
    [isFlowType, pegasusFlowStatuses]
  );

  const showBothTables = useMemo(
    () => showTimePegasusTable && showFlowPegasusTable,
    [showTimePegasusTable, showFlowPegasusTable]
  );

  return (
    <div
      className={`flex justify-start flex-col h-full overflow-x-hidden ${
        showFlowPegasusTable || showTimePegasusTable ? 'overflow-y-auto' : 'overflow-hidden'
      }`}
      style={{ minHeight: showFlowPegasusTable || showTimePegasusTable ? 160 : 80 }}
      data-testid={`${dataTestId}-wrapper`}
    >
      {!pegasusData || isEmpty(pegasusData) ? (
        <LoadingData dataTestId={`${dataTestId}-loading`} />
      ) : showFlowPegasusTable || showTimePegasusTable ? (
        <div className={`flex flex-col my-1 ${displayAsColBreakpoint}:flex-row`}>
          {showTimePegasusTable && (
            <div
              className={`mb-1 ${displayAsColBreakpoint}:mr-2 w-full ${
                showBothTables ? `${displayAsColBreakpoint}:w-1/2` : 'lg:w-3/4'
              }`}
              data-testid={`${dataTestId}-time-table-wrapper`}
            >
              <Typography variant="subtitle1" data-testid={`${dataTestId}-time-table-status`}>
                <Trans id="[Pegasus.PegasusTimeStatus]">Control Mode: Time</Trans>
              </Typography>
              <Typography variant="subtitle2" data-testid={`${dataTestId}-time-table-last-date-received`}>
                <Trans id="[Pegasus.LastDateReceived]">Last date received</Trans>
                {': '}
                {formatLatestDateReceived(pegasusTimeStatuses, formatDate)}
              </Typography>
              <PegasusTimeTable
                data={pegasusTimeStatuses ?? []}
                setData={() => {}}
                isForPmzDetails={isForPmzDetails}
                dataTestId={`${dataTestId}-time-table`}
              />
            </div>
          )}
          {showFlowPegasusTable && (
            <div
              className={`mb-1 w-full ${showBothTables ? `${displayAsColBreakpoint}:w-1/2` : 'lg:w-3/4'}`}
              data-testid={`${dataTestId}-flow-table-wrapper`}
            >
              <Typography variant="subtitle1" data-testid={`${dataTestId}-flow-table-status`}>
                <Trans id="[Pegasus.PegasusFlowStatus]">Control Mode: Flow</Trans>
                {': '}
              </Typography>
              <Typography variant="subtitle2" data-testid={`${dataTestId}-flow-table-last-date-received`}>
                <Trans id="[Pegasus.LastDateReceived]">Last date received</Trans>
                {': '}
                {formatLatestDateReceived(pegasusFlowStatuses, formatDate)}
              </Typography>
              <PegasusFlowTable
                data={
                  pegasusFlowStatuses?.map((el) => ({
                    ...el,
                    uiFlow: Number(el.flow)?.toFixed(2),
                    uiPressure: Number(el.pressure)?.toFixed(2),
                  })) ?? []
                }
                setData={() => {}}
                dataTestId={`${dataTestId}-flow-table`}
              />
            </div>
          )}
        </div>
      ) : (
        <div className="flex justify-center items-center h-full" style={{ minHeight: 76 }}>
          <Typography style={{ fontSize: 18 }} data-testid={`${dataTestId}-no-data`}>
            <Trans id="[Pegasus.NoPegasusTablesToDisplay]">No pegasus tables to display</Trans>
          </Typography>
        </div>
      )}
    </div>
  );
};
